import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API, useAuth } from "@/App";

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying, success, error, expired
  const [message, setMessage] = useState("");
  const token = searchParams.get("token");
  const firedRef = useRef(false);

  const goToLogin = () => {
    logout();
    navigate("/login");
  };

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("No verification token provided.");
      return;
    }
    // Guard against React.StrictMode double-invoke (dev mode fires effects twice).
    // The backend verify-email endpoint consumes the token on first call; a second
    // call would then fail with "Invalid or expired" and incorrectly show "Link Expired".
    if (firedRef.current) return;
    firedRef.current = true;

    const verifyEmail = async () => {
      try {
        const response = await axios.post(`${API}/auth/verify-email`, { token });
        setStatus("success");
        setMessage(response.data.message);
      } catch (error) {
        const errorDetail = error.response?.data?.detail || "Verification failed";
        // Only flag as expired if backend explicitly says the token was expired.
        // "Invalid or expired verification token" (token not found) → error, not expired.
        if (errorDetail.toLowerCase().includes("has expired")) {
          setStatus("expired");
        } else {
          setStatus("error");
        }
        setMessage(errorDetail);
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-screen bg-aura-bg flex items-center justify-center px-6 relative">
      {/* Background effects */}
      <div className="noise-overlay fixed inset-0 pointer-events-none" />
      <div className="grid-bg fixed inset-0 pointer-events-none opacity-30" />
      <div className="absolute inset-0 bg-aura-glow opacity-20" />

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <svg width="48" height="48" viewBox="0 0 100 100" className="glow">
            <defs>
              <radialGradient id="ball-verify" cx="30%" cy="30%">
                <stop offset="0%" stopColor="#444"/>
                <stop offset="100%" stopColor="#000"/>
              </radialGradient>
            </defs>
            <circle cx="50" cy="50" r="45" fill="url(#ball-verify)" />
            <circle cx="50" cy="50" r="18" fill="white"/>
            <text x="50" y="58" textAnchor="middle" fontSize="22" fontWeight="bold" fill="black">8</text>
          </svg>
          <span className="font-heading text-3xl font-bold text-white">AURA</span>
        </Link>

        {/* Card */}
        <div className="card-aura p-8 text-center">
          {status === "verifying" && (
            <>
              <Loader2 className="w-16 h-16 text-aura-emerald mx-auto mb-6 animate-spin" />
              <h1 className="font-heading text-2xl font-bold text-white mb-2">Verifying Email</h1>
              <p className="text-aura-text-secondary">Please wait while we verify your email address...</p>
            </>
          )}

          {status === "success" && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <CheckCircle className="w-16 h-16 text-aura-emerald mx-auto mb-6" />
              </motion.div>
              <h1 className="font-heading text-2xl font-bold text-white mb-2">Email Verified!</h1>
              <p className="text-aura-text-secondary mb-6">{message}</p>
              <Button 
                className="w-full btn-primary py-6"
                onClick={goToLogin}
                data-testid="go-to-login-btn"
              >
                Continue to Login
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
              </motion.div>
              <h1 className="font-heading text-2xl font-bold text-white mb-2">Verification Failed</h1>
              <p className="text-aura-text-secondary mb-6">{message}</p>
              <div className="space-y-3">
                <Button 
                  className="w-full btn-primary py-6"
                  onClick={goToLogin}
                >
                  Go to Login
                </Button>
                <Link to="/" className="block text-aura-text-secondary hover:text-white transition-colors">
                  Return to Home
                </Link>
              </div>
            </>
          )}

          {status === "expired" && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <Mail className="w-16 h-16 text-aura-gold mx-auto mb-6" />
              </motion.div>
              <h1 className="font-heading text-2xl font-bold text-white mb-2">Link Expired</h1>
              <p className="text-aura-text-secondary mb-6">
                This verification link has expired. Please request a new one from the login page.
              </p>
              <Button 
                className="w-full btn-gold py-6"
                onClick={goToLogin}
                data-testid="go-to-login-expired-btn"
              >
                Go to Login
              </Button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyEmailPage;
